<script setup>
import { ref, computed, watch } from 'vue'
import OfferingsSelection from '@/Components/Forms/OfferingsSelection.vue'
import PricingPlanSelector from '@/Components/Forms/PricingPlanSelector.vue'
import SelectionSummary from '@/Components/Forms/SelectionSummary.vue'
import ProTipsCard from '@/Components/Forms/ProTipsCard.vue'
import HelpSupportCard from '@/Components/Forms/HelpSupportCard.vue'
// Hidden for now
// import AdditionalServicesSection from '@/Components/Forms/AdditionalServicesSection.vue'
import NavigationButtons from '@/Components/Onboarding/NavigationButtons.vue'

const props = defineProps({
  offerings: {
    type: Array,
    required: true
  },
  additionalServices: {
    type: Array,
    required: true
  },
  application: Object,
  slug: String
})

const emit = defineEmits(['next', 'back'])

/**
 * The applicant picks products; the application still stores the capabilities
 * behind them, because the scheduler, the student filters and the listing stats
 * all match on teaching method slugs.
 */
const selectedOfferings = ref([])
const selectedAdditionalServices = ref([])
const aboutMe = ref('')
const offeringsRef = ref(null)
const loading = ref(false)
const errors = ref({})

const selectedMethods = computed(() => {
  const ids = props.offerings
    .filter((offering) => selectedOfferings.value.includes(offering.key))
    .flatMap((offering) => offering.method_ids)

  return [...new Set(ids)]
})

// Computed for character count
const aboutMeCharCount = computed(() => aboutMe.value.length)
const aboutMeMinChars = 100
const aboutMeMaxChars = 2000

// Populate from application data
const populateFromApplication = (app) => {
  if (!app) return

  /**
   * An offering is read back from the capability that identifies it. Study
   * material belongs to both products, so it cannot say which was chosen.
   */
  const savedMethodIds = app.teaching_method_ids || []

  selectedOfferings.value = props.offerings
    .filter((offering) => savedMethodIds.includes(offering.primary_method_id))
    .map((offering) => offering.key)

  selectedAdditionalServices.value = app.additional_services || []
  aboutMe.value = app.about_me || ''
}

// Watch for application prop changes
watch(() => props.application, (newApp) => {
  if (newApp) {
    populateFromApplication(newApp)
  }
}, { immediate: true })

/**
 * Whether the selection amounts to the one plan Bindle sells.
 *
 * This used to be inferred by matching teaching method names — `includes
 * ('group')` meant Bindle Live, `includes('study material')` meant the library
 * plan — so renaming a row in the database silently changed a tutor's pricing.
 * Each offering declares its tier. Study material does not imply a plan of its
 * own: it comes with the product rather than being sold beside it.
 */
const hasGoldMethods = computed(() => props.offerings
  .filter((offering) => selectedOfferings.value.includes(offering.key))
  .some((offering) => offering.tier === 'gold')
)

/**
 * The summary lists what the applicant chose, so it lists the two products
 * rather than the capabilities underneath them.
 */
const offeringsAsSummaryItems = computed(() =>
  props.offerings.map((offering) => ({
    id: offering.key,
    name: offering.name,
    icon_name: offering.icon_name,
  }))
)

const validateForm = () => {
  let isValid = true

  if (offeringsRef.value && !offeringsRef.value.validate()) {
    isValid = false
  }

  return isValid
}

const saveDraft = async () => {
  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/services-offered', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        is_draft: true, // Indicate this is a draft save
        teaching_method_ids: selectedMethods.value,
        additional_services: selectedAdditionalServices.value,
        about_me: aboutMe.value || null
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save draft. Please try again.' }
      }
      loading.value = false
      return
    }

    loading.value = false
    // Says where the draft actually lives. It is keyed to a slug in
    // localStorage, so it does not follow the applicant to another
    // browser or device — "saved successfully" implied otherwise.
    errors.value = { success: 'Draft saved. It is kept in this browser on this device, so come back on the same one to carry on.' }
    setTimeout(() => {
      errors.value = {}
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred while saving draft. Please try again.' }
    loading.value = false
  }
}

const submitForm = async () => {
  if (!validateForm()) {
    setTimeout(() => {
      const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
      const firstVisibleError = Array.from(errorMessages).find(el =>
        el.offsetParent !== null && el.textContent.trim().length > 1
      )
      if (firstVisibleError) {
        firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, 100)
    return
  }

  loading.value = true
  errors.value = {}

  try {
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'Accept': 'application/json',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    }

    if (props.slug) {
      headers['X-Tutor-Slug'] = props.slug
    }

    const response = await fetch('/become-a-tutor/services-offered', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        teaching_method_ids: selectedMethods.value,
        additional_services: selectedAdditionalServices.value,
        about_me: aboutMe.value || null
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        // Flatten Laravel's array-based error format to strings
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key])
            ? data.errors[key][0]
            : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false

      // Scroll to the first visible error message
      setTimeout(() => {
        const errorMessages = document.querySelectorAll('p.text-red-500, div.text-red-500')
        const firstVisibleError = Array.from(errorMessages).find(el =>
          el.offsetParent !== null && el.textContent.trim().length > 1
        )
        if (firstVisibleError) {
          firstVisibleError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }

    loading.value = false
    emit('next', data)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}

const goBack = () => {
  emit('back')
}
</script>

<template>
  <div class="grid lg:grid-cols-3 gap-8">
    <!-- Main Content -->
    <div class="lg:col-span-2">
      <form @submit.prevent="submitForm" novalidate class="space-y-8">
        <!-- Success Message -->
        <div v-if="errors.success" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">check_circle</span>
          </div>
          <div>
            <p class="text-green-900 font-semibold">Success</p>
            <p class="text-green-700 text-sm">{{ errors.success }}</p>
          </div>
        </div>

        <!-- General Error Message -->
        <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
          <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
            <span class="material-symbols-outlined text-white text-xl">error</span>
          </div>
          <div>
            <p class="text-red-900 font-semibold">Error</p>
            <p class="text-red-700 text-sm">{{ errors.general }}</p>
          </div>
        </div>

        <!-- Intro Section -->
        <section id="intro-section">
          <h2 class="text-3xl font-bold text-gray-900 mb-3">What would you like to offer?</h2>
          <p class="text-lg text-gray-600">Choose either or both. Your study material comes with whichever you pick.</p>
        </section>

        <!-- Offerings Selection Component -->
        <OfferingsSelection
          ref="offeringsRef"
          :offerings="offerings"
          :model-value="selectedOfferings"
          @update:model-value="(val) => selectedOfferings = val"
          title="What You Offer"
          description="Pick the products you want to teach on Bindle"
          :required="true"
          :errors="errors"
        />

        <!-- Bio Section -->
        <section id="bio-section" class="p-0 md:rounded-2xl md:border-2 md:border-line md:bg-surface md:p-8">
          <div class="mb-6 flex items-start gap-4">
            <!--
              `md:grid`, not `md:block`. This tile asked for `flex` and then for
              `block` at the same breakpoint, and the later one won — so the two
              centring instructions on it applied to nothing and the glyph sat in
              the top-left corner of its own circle rather than the middle of it.
              The drop shadow under it was purple, on a teal tile.
            -->
            <div class="hidden size-12 shrink-0 place-items-center rounded-xl bg-brand shadow-md md:grid">
              <span class="material-symbols-outlined text-2xl text-brand-contrast">person_edit</span>
            </div>
            <div>
              <h3 class="text-lg font-bold text-ink md:text-xl">Tell Students About Yourself</h3>
              <p class="mt-1 text-muted">Write a compelling bio that helps students understand who you are and what makes you a great tutor.</p>
            </div>
          </div>

          <div class="space-y-4 max-sm:rounded-2xl max-sm:border-2 max-sm:border-line max-sm:bg-surface max-sm:p-8">
            <div>
              <label for="about_me" class="mb-2 block text-sm font-semibold text-ink">
                Your Bio
                <span class="font-normal text-subtle">(Optional)</span>
              </label>
              <textarea
                id="about_me"
                v-model="aboutMe"
                rows="6"
                :maxlength="aboutMeMaxChars"
                class="w-full resize-none rounded-xl border-2 border-line bg-surface px-4 py-3 text-ink transition-all duration-200 focus:border-brand focus:ring-4 focus:ring-brand-soft"
                placeholder="Share your teaching philosophy, experience, and what makes your tutoring unique. Students love hearing about your background and approach to helping them succeed..."
              ></textarea>
              <div class="flex justify-between items-center mt-2">
                <p class="text-sm text-muted">
                  <span v-if="aboutMeCharCount > 0 && aboutMeCharCount < aboutMeMinChars" class="text-warning">
                    Minimum {{ aboutMeMinChars }} characters recommended ({{ aboutMeMinChars - aboutMeCharCount }} more needed)
                  </span>
                  <span v-else-if="aboutMeCharCount === 0" class="text-subtle">
                    Write at least {{ aboutMeMinChars }} characters for best results
                  </span>
                  <span v-else class="text-success">
                    Great length!
                  </span>
                </p>
                <p class="text-sm" :class="aboutMeCharCount > aboutMeMaxChars * 0.9 ? 'text-warning' : 'text-subtle'">
                  {{ aboutMeCharCount }}/{{ aboutMeMaxChars }}
                </p>
              </div>
              <p v-if="errors.about_me" class="mt-1 text-sm text-danger">{{ errors.about_me }}</p>
            </div>

            <!-- Tips for writing a good bio -->
            <div class="mt-4 rounded-xl bg-brand-soft p-4">
              <h4 class="mb-2 flex items-center text-sm font-semibold text-brand-soft-text">
                <span class="material-symbols-outlined mr-2 text-lg text-brand">lightbulb</span>
                Tips for a Great Bio
              </h4>
              <ul class="space-y-1 text-sm text-brand-soft-text">
                <li class="flex items-start">
                  <span class="mr-2 text-brand">•</span>
                  Mention your qualifications and relevant experience
                </li>
                <li class="flex items-start">
                  <span class="mr-2 text-brand">•</span>
                  Describe your teaching style and approach
                </li>
                <li class="flex items-start">
                  <span class="mr-2 text-brand">•</span>
                  Share what you enjoy about tutoring
                </li>
                <li class="flex items-start">
                  <span class="mr-2 text-brand">•</span>
                  Include any achievements or specializations
                </li>
              </ul>
            </div>
          </div>
        </section>

        <!-- Additional Services Section - Hidden for now -->
        <!-- <AdditionalServicesSection
          v-model="selectedAdditionalServices"
          :services="additionalServices"
        /> -->

      </form>
    </div>

    <!-- Right Sidebar - Only visible on large screens -->
    <aside id="sidebar-info" class="hidden lg:block lg:col-span-1 self-start sticky top-24">
      <!-- Tiered Pricing Info -->
      <PricingPlanSelector
        :has-gold-methods="hasGoldMethods"
        class="mb-6"
      />

      <!-- Selection Summary -->
      <SelectionSummary
        :selected-methods="selectedOfferings"
        :all-methods="offeringsAsSummaryItems"
        title="Your Selection"
        class="mb-6"
      />

      <!-- Pro Tips -->
      <ProTipsCard />

      <!-- Help Section -->
      <HelpSupportCard />
    </aside>
  </div>

  <!-- Navigation Buttons -->
  <NavigationButtons
    :loading="loading"
    :show-previous="true"
    primary-button-text="Continue to Pricing & Availability"
    primary-button-loading-text="Saving..."
    @previous="goBack"
    @save-draft="saveDraft"
    @submit="submitForm"
  />
</template>
